import { useMemo } from 'react';
import type { NetworkType, PolicyFunction } from '@mls-wallet/shared-types';
import { getAddress } from 'viem';
import { useContractAbi } from '@/hooks/transaction/use-contract-abi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  WalletConnectTransactionData,
  WalletConnectTransactionError,
} from './types';
import type { DecodedContractTransactionPayload } from '../types';
import {
  decodeContractTransactionImport,
  isCalldataFunctionAllowed,
} from '../raw-transaction-import';

type WalletConnectTransactionDialogProps = {
  open: boolean;
  network: NetworkType | undefined;
  transactionData: WalletConnectTransactionData | null;
  transactionError: WalletConnectTransactionError | null;
  connectedDappName?: string | undefined;
  policyAllowedContractAddress: string | null | undefined;
  policyAllowedFunctions: PolicyFunction[];
  onClose: () => void;
  onReject: () => Promise<void>;
  onImportTransaction: (
    payload: DecodedContractTransactionPayload
  ) => Promise<void>;
};

type PolicyValidationMessage = {
  title: string;
  incomingLabel: string;
  incomingValue: string;
  allowedLabel: string;
  allowedValue: string;
};

/**
 * Dialog that appears when a user sends a transaction from the dapp.
 * Will also show error messages for unsupported requests and policy violations.
 */
export function WalletConnectTransactionDialog({
  open,
  network,
  transactionData,
  transactionError,
  connectedDappName,
  policyAllowedContractAddress,
  policyAllowedFunctions,
  onClose,
  onReject,
  onImportTransaction,
}: WalletConnectTransactionDialogProps): React.ReactElement | null {
  const {
    contractDisplayName,
    implementationFunctions,
    proxyFunctions,
    isLoading,
    errorMessage: abiErrorMessage,
    error: abiNetworkError,
  } = useContractAbi(
    open && transactionData && network
      ? { address: transactionData.to, networkType: network }
      : null
  );
  const hasAbiLookupError = Boolean(abiErrorMessage || abiNetworkError);

  /**
   * Parse out the transaction details incoming from walletconnect
   */
  const importPayload = useMemo(() => {
    if (!transactionData || !contractDisplayName) {
      return null;
    }

    try {
      // Share JSON import decoding so addresses, booleans, and payable zero
      // values arrive in the same editable, validated form state.
      const result = decodeContractTransactionImport({
        transaction: {
          to: getAddress(transactionData.to),
          data: transactionData.data,
          value: BigInt(transactionData.value).toString(),
        },
        contractName: contractDisplayName,
        implementationFunctions,
        proxyFunctions,
      });
      return result.status === 'decoded' ? result.details : null;
    } catch (error) {
      console.error('Failed to parse WalletConnect transaction payload', {
        topic: transactionData.topic,
        to: transactionData.to,
        error,
      });
      return null;
    }
  }, [
    contractDisplayName,
    implementationFunctions,
    proxyFunctions,
    transactionData,
  ]);

  /**
   * Check if the transaction is allowed by the policy
   */
  const policyValidationMessages = useMemo(() => {
    if (!transactionData) {
      return [];
    }

    const messages: PolicyValidationMessage[] = [];
    let incomingContract = importPayload?.contractAddress ?? null;
    if (!incomingContract) {
      try {
        incomingContract = getAddress(transactionData.to);
      } catch {
        messages.push({
          title: 'Invalid contract address',
          incomingLabel: 'Incoming contract:',
          incomingValue: transactionData.to,
          allowedLabel: 'Expected format:',
          allowedValue: 'Valid EVM contract address',
        });
        return messages;
      }
    }

    let hasContractMismatch = false;

    // Case: the policy allows a specific contract address, check if the incoming contract matches
    if (policyAllowedContractAddress) {
      let allowedContract: string;
      try {
        allowedContract = getAddress(policyAllowedContractAddress);
      } catch {
        hasContractMismatch = true;
        messages.push({
          title: 'Invalid policy contract address',
          incomingLabel: 'Incoming contract:',
          incomingValue: incomingContract,
          allowedLabel: 'Policy allowed contract:',
          allowedValue: policyAllowedContractAddress,
        });
        return messages;
      }

      if (incomingContract.toLowerCase() !== allowedContract.toLowerCase()) {
        hasContractMismatch = true;
        messages.push({
          title: 'Contract mismatch',
          incomingLabel: 'Incoming contract:',
          incomingValue: incomingContract,
          allowedLabel: 'Policy allowed contract:',
          allowedValue: allowedContract,
        });
      }
    }

    // Case: no contract mismatch and ABI is available, check if the incoming function is allowed by the policy
    if (
      !hasContractMismatch &&
      !hasAbiLookupError &&
      policyAllowedFunctions.length > 0
    ) {
      const incomingFunctionSelector = transactionData.data
        .slice(0, 10)
        .toLowerCase();
      const incomingFunctionValue = importPayload
        ? `${importPayload.selectedFunction.name} (${incomingFunctionSelector})`
        : incomingFunctionSelector;
      // A matching selector is insufficient when the ABI cannot decode its
      // arguments; keep that malformed call in the blocking-error path.
      const isFunctionAllowed =
        importPayload !== null &&
        isCalldataFunctionAllowed(transactionData, policyAllowedFunctions);

      if (!isFunctionAllowed) {
        messages.push({
          title: 'Function mismatch',
          incomingLabel: 'Incoming function:',
          incomingValue: incomingFunctionValue,
          allowedLabel: 'Policy allowed function(s):',
          allowedValue: policyAllowedFunctions
            .map((func) => `${func.name} (${func.selector})`)
            .join(', '),
        });
      }
    }

    return messages;
  }, [
    importPayload,
    hasAbiLookupError,
    policyAllowedContractAddress,
    policyAllowedFunctions,
    transactionData,
  ]);

  if (!transactionData && !transactionError) {
    return null;
  }

  const hasRuntimeRequestError = transactionError !== null;
  const hasPolicyViolations = policyValidationMessages.length > 0;
  const dappNameForCopy = connectedDappName ?? 'connected dApp';
  const hasBlockingError =
    hasRuntimeRequestError || hasPolicyViolations || hasAbiLookupError;

  // Handler for the primary button. This will either reject the transaction, import the transaction, or close the dialog
  const handlePrimaryAction = async () => {
    if (hasRuntimeRequestError) {
      onClose();
      return;
    }

    // Case: there are policy violations or ABI lookup errors, reject the transaction and close the dialog
    if (hasPolicyViolations || hasAbiLookupError) {
      try {
        await onReject();
      } catch {
        // Do nothing on error
      } finally {
        onClose();
      }
      return;
    }

    // Case: there is an import payload, import the transaction
    if (importPayload) {
      await onImportTransaction(importPayload);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="[&>button.absolute]:hidden">
        <DialogHeader>
          {/* Case: request from dapp failed, show the error message */}
          {hasRuntimeRequestError ? (
            <>
              <DialogTitle>Request from {dappNameForCopy} failed</DialogTitle>
              <DialogDescription>
                {getWalletConnectTransactionErrorMessage(
                  transactionError as WalletConnectTransactionError,
                  dappNameForCopy
                )}
              </DialogDescription>
            </>
          ) : hasAbiLookupError ? (
            <>
              <DialogTitle>
                Unable to import transaction from {dappNameForCopy}
              </DialogTitle>
              <DialogDescription>
                We could not load the contract ABI for {transactionData?.to}, so
                this WalletConnect request cannot be decoded for import.
              </DialogDescription>
            </>
          ) : (
            // Case: request from dapp is allowed, show the import transaction dialog
            <>
              <DialogTitle>
                Import transaction from {dappNameForCopy}?
              </DialogTitle>
              <DialogDescription>
                {dappNameForCopy} requested a contract interaction on{' '}
                {transactionData?.to}
              </DialogDescription>
            </>
          )}
        </DialogHeader>
        {/* Case: ABI lookup failed, show the ABI error details */}
        {!hasRuntimeRequestError && hasAbiLookupError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <p className="font-medium">Contract ABI lookup failed</p>
            <p className="mt-1">
              {abiErrorMessage ?? 'Failed to fetch contract ABI.'}
            </p>
          </div>
        )}
        {/* Case: policy violations, show the policy violations */}
        {!hasRuntimeRequestError &&
          !hasAbiLookupError &&
          policyValidationMessages.length > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <p className="text-sm font-medium text-destructive">
                This WalletConnect import is not allowed by the selected policy.
              </p>
              <div className="mt-2 flex flex-col gap-3 text-sm text-destructive">
                {policyValidationMessages.map((message) => (
                  <div key={message.title}>
                    <p className="font-medium">{message.title}</p>
                    <p>
                      {message.incomingLabel} {message.incomingValue}
                    </p>
                    <p>
                      {message.allowedLabel} {message.allowedValue}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        <DialogFooter>
          {/* Case: no errors, show the reject button */}
          {!hasBlockingError && (
            <Button variant="outline" onClick={onReject}>
              Reject
            </Button>
          )}
          {/* Case: when there are errors, show close button, otherwise show import button */}
          <Button
            onClick={() => void handlePrimaryAction()}
            disabled={isLoading || (!hasBlockingError && !importPayload)}
          >
            {hasBlockingError ? 'Close' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Show a user the error message for the transaction error
 */
function getWalletConnectTransactionErrorMessage(
  transactionError: WalletConnectTransactionError,
  dappName: string
): string {
  switch (transactionError) {
    case WalletConnectTransactionError.WRONG_ACCOUNT:
      return `${dappName} sent a request for a different account than your connected wallet.`;
    case WalletConnectTransactionError.WRONG_NETWORK:
      return `${dappName} sent a request for a different blockchain network than your current selection.`;
    case WalletConnectTransactionError.NATIVE_SEND_UNSUPPORTED:
      return `${dappName} requested a native token send, which is not supported in this WalletConnect flow.`;
    case WalletConnectTransactionError.UNSUPPORTED_METHOD:
      return `${dappName} requested a WalletConnect method that is not supported.`;
    default:
      return `WalletConnect request from ${dappName} failed.`;
  }
}
